import React from 'react';
import {
  Agent,
  ChannelType,
  Integration,
  IntegrationStatus,
} from '../../model';
import { Input, Badge } from '../../ui';
import { integrationStatusConfigs } from '../../ui/Badge/statusConfigs';
import './IntegrationForm.scss';

export interface IntegrationFormProps {
  integration: Integration | null;
  agents: Agent[];
  isNewIntegration: boolean;
  onIntegrationChange: (integration: Integration) => void;
  onSave: () => void;
  onDelete?: () => void;
  onToggleStatus?: () => void;
  isSaveDisabled: boolean;
  isDeleteDisabled: boolean;
  isStatusToggleDisabled: boolean;
}

const IntegrationForm: React.FC<IntegrationFormProps> = ({
  integration,
  agents,
  isNewIntegration,
  onIntegrationChange,
  onSave,
  onDelete,
  onToggleStatus,
  isSaveDisabled,
  isDeleteDisabled,
  isStatusToggleDisabled,
}) => {
  if (!integration) return null;

  const isActive = integration.status === IntegrationStatus.ACTIVE;
  const isDisabled = isActive;

  return (
    <div className="integration-form">
      <div className="integration-form__header">
        <h1>
          {isNewIntegration ? 'Create New Integration' : 'Integration Details'}
        </h1>
        <div className="status-badge-container">
          <Badge
            status={integration.status || ''}
            statusConfigs={integrationStatusConfigs}
          />
        </div>
        <div className="buttons-container">
          <button
            onClick={onSave}
            disabled={isSaveDisabled}
            className="save-button">
            Save
          </button>
          {!isNewIntegration && onDelete && (
            <button
              onClick={onDelete}
              disabled={isDeleteDisabled}
              className="delete-button">
              Delete
            </button>
          )}
        </div>
      </div>

      <div className="integration-form__content">
        <div className="form-group">
          <Input
            label="Name"
            value={integration.name}
            onChange={value =>
              onIntegrationChange({ ...integration, name: value })
            }
            placeholder="Enter integration name"
            disabled={isDisabled}
            fullWidth
          />
        </div>

        <div className="form-group">
          <label className="form-label">
            Type:
            <select
              value={integration.type}
              onChange={e =>
                onIntegrationChange({
                  ...integration,
                  type: e.target.value as ChannelType,
                })
              }
              className="select-field"
              disabled={!isNewIntegration}>
              <option value={ChannelType.TELEGRAM}>Telegram</option>
              <option value={ChannelType.WEBHOOK}>Webhook</option>
            </select>
          </label>
        </div>

        <div className="form-group">
          <label className="form-label">
            Agent:
            <select
              value={integration.agentUuid || ''}
              onChange={e =>
                onIntegrationChange({
                  ...integration,
                  agentUuid: e.target.value || undefined,
                })
              }
              className="select-field"
              disabled={isDisabled}>
              <option value="">Select an Agent</option>
              {agents.map(agent => (
                <option key={agent.uuid} value={agent.uuid}>
                  {agent.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        {integration.type === ChannelType.TELEGRAM && (
          <div className="form-group">
            <Input
              label="Bot Token"
              value={integration.botToken}
              onChange={value =>
                onIntegrationChange({ ...integration, botToken: value })
              }
              placeholder="Enter bot token"
              disabled={isDisabled}
              fullWidth
            />
          </div>
        )}

        {integration.type === ChannelType.WEBHOOK && (
          <>
            <div className="form-group">
              <Input
                label="Secret Token"
                value={integration.secretToken || ''}
                onChange={value =>
                  onIntegrationChange({
                    ...integration,
                    secretToken: value,
                  })
                }
                placeholder="Enter secret token"
                disabled={isDisabled}
                fullWidth
              />
            </div>
            <div className="form-group">
              <Input
                label="Response Webhook"
                value={integration.responseWebhook || ''}
                onChange={value =>
                  onIntegrationChange({
                    ...integration,
                    responseWebhook: value,
                  })
                }
                placeholder="Enter response webhook URL"
                disabled={isDisabled}
                fullWidth
              />
            </div>
          </>
        )}

        {!isNewIntegration && onToggleStatus && (
          <div className="form-group">
            <button
              onClick={onToggleStatus}
              disabled={isStatusToggleDisabled}
              className={`toggle-status-button ${isActive ? 'deactivate' : 'activate'}`}>
              {isActive ? 'Deactivate' : 'Activate'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default IntegrationForm;
